import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import type { BadgeIntent } from './Badge.js';
import { IconAlert, IconAlertCircle, IconCheck, IconInfo, type IconProps } from './Icon.js';

/**
 * Banner — a message about a region of the page.
 *
 * Used for the thing v1 did with `<div className="banner warn">`: the platform
 * refusing a period close, an account that cannot be archived, a voucher that
 * does not balance.
 *
 * ── WHY THE MESSAGE IS PASSED THROUGH VERBATIM ────────────────────────────
 *
 * The platform's refusals name the period, the balance, or the rule that
 * blocked the change. That text is more useful than anything the UI could
 * invent, so this component never rewrites it — it only frames it. Hence
 * `whitespace-pre-wrap`: server messages carry deliberate line breaks and
 * collapsing them turns a readable explanation into a paragraph.
 */

const ICON: Record<BadgeIntent, (p: IconProps) => ReactNode> = {
  neutral: IconInfo,
  info: IconInfo,
  success: IconCheck,
  warning: IconAlert,
  danger: IconAlertCircle,
};

const INTENT: Record<BadgeIntent, string> = {
  neutral: 'bg-neutral-bg border-neutral-border text-neutral-fg',
  info: 'bg-info-bg border-info-border text-info-fg',
  success: 'bg-success-bg border-success-border text-success-fg',
  warning: 'bg-warning-bg border-warning-border text-warning-fg',
  danger: 'bg-danger-bg border-danger-border text-danger-fg',
};

export function Banner({
  intent = 'info',
  title,
  actions,
  className,
  children,
}: {
  intent?: BadgeIntent;
  title?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  const Icon = ICON[intent];

  return (
    <div
      // Errors and warnings interrupt; confirmations wait their turn. `alert`
      // on a success message would talk over whatever the user is reading.
      role={intent === 'danger' || intent === 'warning' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-3 rounded-(--card-radius) border px-4 py-3',
        // Drops in from above: a banner interrupts the content below it, and
        // arriving from that direction says so.
        'banner-motion',
        INTENT[intent],
        className,
      )}
    >
      <Icon size={16} className="mt-px shrink-0" />
      <div className="min-w-0 flex-1">
        {title && <p className="text-md font-semibold leading-snug">{title}</p>}
        {children && (
          <div
            className={cn(
              'text-md leading-snug whitespace-pre-wrap',
              title && 'mt-1 opacity-90',
            )}
          >
            {children}
          </div>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
